import { useState, useEffect, useCallback, useRef } from "react";
import { Loader2 } from "lucide-react";
import { authFetch } from "../../utils/authFetch";
import {
  type TreeNode, type ChangeEntry, type DetailData, type MoveMapping,
} from "./TreeDiffViewer";
import TwoColumnDiffTree from "./TwoColumnDiffTree";

/* ── Props ── */

export interface CommitDiffPanelProps {
  /** Commit ID for the "before" state (null = root commit, show all as added) */
  commitA: string | null;
  /** Commit ID for the "after" state */
  commitB: string;
  /** Project slug for API calls */
  projectSlug: string;
  /** Branch ID — needed for tree-diff API URL path */
  branchId: string;
  /** Labels for the before/after columns */
  beforeLabel?: string;
  afterLabel?: string;
  /** Slug paths of conflicting files to highlight in the tree */
  conflictPaths?: string[];
}

export default function CommitDiffPanel({
  commitA, commitB, projectSlug, branchId,
  beforeLabel = "Before", afterLabel = "After", conflictPaths,
}: CommitDiffPanelProps) {
  const [loading, setLoading] = useState(true);
  const [beforeNodes, setBeforeNodes] = useState<TreeNode[]>([]);
  const [afterNodes, setAfterNodes] = useState<TreeNode[]>([]);
  const [beforeRootOrder, setBeforeRootOrder] = useState<string[]>([]);
  const [afterRootOrder, setAfterRootOrder] = useState<string[]>([]);
  const [changes, setChanges] = useState<ChangeEntry[]>([]);
  const [moves, setMoves] = useState<MoveMapping[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<DetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Track current diff context for node-diff calls
  const diffContextRef = useRef<{ commitA: string | null; commitB: string }>({ commitA, commitB });

  // Fetch tree-diff
  useEffect(() => {
    if (!projectSlug || !branchId || !commitB) return;
    let cancelled = false;
    setLoading(true);
    setSelectedDetail(null);
    diffContextRef.current = { commitA, commitB };

    (async () => {
      try {
        // Root commit (no parent): fetch commitB→commitB and mark all as added
        const effectiveA = commitA || commitB;
        const url = `/api/projects/${projectSlug}/studio/branches/${branchId}/tree-diff/${effectiveA}/${commitB}`;
        const res = await authFetch(url);
        if (!res.ok || cancelled) { if (!cancelled) setLoading(false); return; }
        const data = await res.json();
        if (cancelled) return;

        if (!commitA) {
          // Root commit — everything is "added"
          const an: TreeNode[] = data.afterNodes || [];
          const aro: string[] = data.afterRootOrder || [];
          setBeforeNodes([]);
          setAfterNodes(an);
          setBeforeRootOrder([]);
          setAfterRootOrder(aro);
          setChanges(an.map(n => ({ slugPath: n.slugPath, actions: ["added"] })));
          setMoves([]);
        } else {
          setBeforeNodes(data.beforeNodes || []);
          setAfterNodes(data.afterNodes || []);
          setBeforeRootOrder(data.beforeRootOrder || []);
          setAfterRootOrder(data.afterRootOrder || []);
          setChanges(data.changes || []);
          setMoves(data.moves || []);
        }
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [commitA, commitB, projectSlug, branchId]);

  // Handle opening detail view via node-diff endpoint
  const handleSelectChange = useCallback(async (change: ChangeEntry) => {
    const ctx = diffContextRef.current;
    if (!projectSlug) return;
    const slugA = change.originalSlugPath || change.slugPath;
    const slugB = change.slugPath;
    const title = change.slugPath.split("/").pop() || change.slugPath;
    setDetailLoading(true);
    setSelectedDetail({ slugPath: change.slugPath, title, change, beforeBody: "", afterBody: "" });
    try {
      const params = new URLSearchParams();
      if (ctx.commitA) { params.set("commit_a", ctx.commitA); params.set("slug_a", slugA); }
      params.set("commit_b", ctx.commitB); params.set("slug_b", slugB);
      const res = await authFetch(`/api/projects/${projectSlug}/studio/node-diff?${params}`);
      if (res.ok) {
        const d = await res.json();
        setSelectedDetail({
          slugPath: change.slugPath,
          title: d.afterTitle || d.beforeTitle || title,
          change,
          beforeBody: d.beforeBody || "",
          afterBody: d.afterBody || "",
          nodeType: d.nodeType,
          beforeTitle: d.beforeTitle,
          afterTitle: d.afterTitle,
          changedAssets: d.changedAssets,
          beforeSpec: d.beforeSpec || null,
          afterSpec: d.afterSpec || null,
          // Support / FAQ metadata (a summary-only change has no body diff)
          beforeMeta: d.beforeMeta ?? null,
          afterMeta: d.afterMeta ?? null,
          sectionType: d.sectionType ?? null,
        });
      }
    } catch { /* keep empty bodies */ }
    setDetailLoading(false);
  }, [projectSlug]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="size-4 animate-spin mr-2" /> Loading diff…
      </div>
    );
  }

  const noChanges = changes.length === 0 && JSON.stringify(beforeRootOrder) === JSON.stringify(afterRootOrder);
  if (noChanges) {
    return (
      <div className="flex-1 flex items-center justify-center py-16 text-[14px] text-slate-400">
        No changes between commits.
      </div>
    );
  }

  return (
    <TwoColumnDiffTree
      beforeNodes={beforeNodes}
      afterNodes={afterNodes}
      beforeRootOrder={beforeRootOrder}
      afterRootOrder={afterRootOrder}
      changes={changes}
      moves={moves}
      beforeLabel={beforeLabel}
      afterLabel={afterLabel}
      onSelectChange={handleSelectChange}
      selectedDetail={selectedDetail}
      detailLoading={detailLoading}
      onClearDetail={() => setSelectedDetail(null)}
      conflictPaths={conflictPaths}
    />
  );
}
